/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlaskit/design-system/use-primitives-text */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import LogOutIcon from '@atlaskit/icon/core/log-out';
import PersonIcon from '@atlaskit/icon/core/person';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getConfig } from '../../../config';

import { messages } from './messages';

type UserAvatarDropdownProps = {
	user: {
		accountId: string;
		name: string;
		picture: string;
	};
};

const styles = cssMap({
	userMenuTrigger: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 0,
		border: 'none',
		backgroundColor: 'transparent',
		cursor: 'pointer',
		borderRadius: token('radius.full'),
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
	userPicture: {
		width: '32px',
		height: '32px',
		borderRadius: token('radius.full'),
		objectFit: 'cover',
		display: 'block',
	},
	dropdownItem: {
		width: '200px',
	},
});

export const UserAvatarDropdown = ({ user }: UserAvatarDropdownProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, ...triggerProps }) => (
				<button
					{...triggerProps}
					ref={triggerRef}
					type="button"
					css={styles.userMenuTrigger}
					aria-label={formatMessage(messages.userAccountMenuAriaLabel, {
						userName: user.name,
					})}
				>
					<img src={user.picture} alt="" css={styles.userPicture} />
				</button>
			)}
			shouldRenderToParent
			placement="bottom-end"
		>
			<DropdownItemGroup>
				<DropdownItem
					onClick={() =>
						window.open(
							`${getConfig().atlassianHomeUrl}/people/${encodeURIComponent(user.accountId)}`,
							'_blank',
						)
					}
				>
					<Inline space="space.150" alignBlock="center" xcss={styles.dropdownItem}>
						<PersonIcon label={formatMessage(messages.profileLabel)} />
						<Text>
							<FormattedMessage {...messages.profileLabel} />
						</Text>
					</Inline>
				</DropdownItem>
			</DropdownItemGroup>
			<DropdownItemGroup hasSeparator>
				<DropdownItem
					onClick={() =>
						window.open(
							`${getConfig().atlassianAccountUrl}/logout?application=twg-microsite-bifrost&continue=${encodeURIComponent(
								getConfig().serviceUrl,
							)}`,
						)
					}
				>
					<Inline space="space.150" alignBlock="center" xcss={styles.dropdownItem}>
						<LogOutIcon label={formatMessage(messages.logoutLabel)} />
						<Text>
							<FormattedMessage {...messages.logoutLabel} />
						</Text>
					</Inline>
				</DropdownItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
